
/// <reference types="vite/client" />
import axios, { AxiosError } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Extract a user-friendly error message from Axios errors or generic errors.
 */
function extractErrorMessage(error: any): string {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.message) return error.message;
    return 'Unknown error';
}

class ApiClient {
    private isRefreshing = false;
    private refreshPromise: Promise<boolean> | null = null;
    private tokenKey: string;
    private refreshKey: string;

    constructor(tokenKey = 'es_access_token', refreshKey = 'es_refresh_token') {
        this.tokenKey = tokenKey;
        this.refreshKey = refreshKey;
    }

    private getHeaders() {
        const token = localStorage.getItem(this.tokenKey);
        return {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
        };
    }

    /**
     * Automatically refresh the access token using the stored refresh token.
     * Returns true if refresh succeeded, false otherwise.
     */
    private async tryRefreshToken(): Promise<boolean> {
        // Deduplicate concurrent refresh attempts
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = (async () => {
            try {
                const refreshToken = localStorage.getItem(this.refreshKey);
                if (!refreshToken) return false;

                const res = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
                const { accessToken, refreshToken: newRefreshToken } = res.data;

                if (accessToken) {
                    localStorage.setItem(this.tokenKey, accessToken);
                    if (newRefreshToken) {
                        localStorage.setItem(this.refreshKey, newRefreshToken);
                    }
                    return true;
                }
                return false;
            } catch {
                return false;
            } finally {
                this.isRefreshing = false;
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    /**
     * Wraps an Axios call. On 401, tries to refresh the token and retry once.
     */
    private async withAutoRefresh<T>(fn: () => Promise<T>): Promise<T> {
        try {
            return await fn();
        } catch (error: any) {
            if (error?.response?.status === 401) {
                const refreshed = await this.tryRefreshToken();
                if (refreshed) {
                    // Retry with new token
                    return await fn();
                }
            }
            throw error;
        }
    }

    from(table: string) {
        const self = this;
        return {
            select(columns: string = '*', options?: { count?: 'exact' | 'planned' | 'estimated' }) {
                const filters: any = { select: columns };
                let order: string | null = null;
                let limit: number | null = null;
                let offset: number | null = null;
                let isSingle = false;

                const builder: any = {
                    eq(column: string, value: any) {
                        filters[column] = value;
                        return builder;
                    },
                    in(column: string, values: any[]) {
                        filters[column] = `in.(${values.join(',')})`;
                        return builder;
                    },
                    neq(column: string, value: any) {
                        filters[`${column}`] = `neq.${value}`;
                        return builder;
                    },
                    gte(column: string, value: any) {
                        filters[`${column}`] = `gte.${value}`;
                        return builder;
                    },
                    lte(column: string, value: any) {
                        filters[`${column}`] = `lte.${value}`;
                        return builder;
                    },
                    gt(column: string, value: any) {
                        filters[`${column}`] = `gt.${value}`;
                        return builder;
                    },
                    lt(column: string, value: any) {
                        filters[`${column}`] = `lt.${value}`;
                        return builder;
                    },
                    ilike(column: string, value: any) {
                        filters[`${column}`] = `ilike.${value}`;
                        return builder;
                    },
                    order(column: string, options?: { ascending?: boolean }) {
                        order = `${column}.${options?.ascending === false ? 'desc' : 'asc'}`;
                        return builder;
                    },
                    limit(n: number) {
                        limit = n;
                        return builder;
                    },
                    offset(n: number) {
                        offset = n;
                        return builder;
                    },
                    single() {
                        isSingle = true;
                        return builder;
                    },
                    maybeSingle() {
                        isSingle = true;
                        return builder;
                    },
                    async then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any) {
                        try {
                            const res = await self.withAutoRefresh(() =>
                                axios.get(`${API_URL}/data/${table}`, {
                                    params: { ...filters, order, limit, offset },
                                    headers: self.getHeaders(),
                                })
                            );
                            let data = res.data;
                            if (isSingle && Array.isArray(data)) {
                                data = data[0] || null;
                            }
                            const result = { data, error: null };
                            return onfulfilled ? onfulfilled(result) : result;
                        } catch (error: any) {
                            const result = { data: null, error: extractErrorMessage(error) };
                            return onfulfilled ? onfulfilled(result) : result;
                        }
                    }
                };
                return builder;
            },
            insert(data: any) {
                const builder: any = {
                    select() {
                        return builder;
                    },
                    single() {
                        return builder;
                    },
                    async then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any) {
                        try {
                            const res = await self.withAutoRefresh(() =>
                                axios.post(`${API_URL}/data/${table}`, data, {
                                    headers: self.getHeaders(),
                                })
                            );
                            const result = { data: res.data, error: null };
                            return onfulfilled ? onfulfilled(result) : result;
                        } catch (error: any) {
                            const result = { data: null, error: extractErrorMessage(error) };
                            return onfulfilled ? onfulfilled(result) : result;
                        }
                    }
                };
                return builder;
            },
            update(data: any) {
                const filters: any = {};
                const builder: any = {
                    eq(column: string, value: any) {
                        filters[column] = value;
                        return builder;
                    },
                    in(column: string, values: any[]) {
                        filters[column] = `in.(${values.join(',')})`;
                        return builder;
                    },
                    neq(column: string, value: any) {
                        return builder;
                    },
                    async then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any) {
                        try {
                            const id = filters['id'] || filters['code'];
                            if (!id) {
                                const result = { data: null, error: 'Update requires an identity filter via .eq("id", value) or .eq("code", value)' };
                                return onfulfilled ? onfulfilled(result) : result;
                            }
                            const res = await self.withAutoRefresh(() =>
                                axios.patch(`${API_URL}/data/${table}/${id}`, data, {
                                    headers: self.getHeaders(),
                                })
                            );
                            const result = { data: res.data, error: null };
                            return onfulfilled ? onfulfilled(result) : result;
                        } catch (error: any) {
                            const result = { data: null, error: extractErrorMessage(error) };
                            return onfulfilled ? onfulfilled(result) : result;
                        }
                    }
                };
                return builder;
            },
            upsert(data: any, options?: { onConflict?: string; ignoreDuplicates?: boolean }) {
                const builder: any = {
                    async then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any) {
                        try {
                            const params: any = {};
                            if (options?.onConflict) params.onConflict = options.onConflict;
                            if (options?.ignoreDuplicates) params.ignoreDuplicates = options.ignoreDuplicates;

                            const res = await self.withAutoRefresh(() =>
                                axios.post(`${API_URL}/data/${table}/upsert`, data, {
                                    params,
                                    headers: self.getHeaders(),
                                })
                            );
                            const result = { data: res.data, error: null };
                            return onfulfilled ? onfulfilled(result) : result;
                        } catch (error: any) {
                            const result = { data: null, error: extractErrorMessage(error) };
                            return onfulfilled ? onfulfilled(result) : result;
                        }
                    }
                };
                return builder;
            },
            delete() {
                const filters: any = {};
                const builder: any = {
                    eq(column: string, value: any) {
                        filters[column] = value;
                        return builder;
                    },
                    in(column: string, values: any[]) {
                        filters[column] = `in.(${values.join(',')})`;
                        return builder;
                    },
                    async then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any) {
                        try {
                            const id = filters['id'] || filters['code'];
                            // Only use the singular /table/id route if it's a simple, non-in filter value
                            if (id && (typeof id !== 'string' || !id.startsWith('in.('))) {
                                const res = await self.withAutoRefresh(() =>
                                    axios.delete(`${API_URL}/data/${table}/${id}`, {
                                        headers: self.getHeaders(),
                                    })
                                );
                                const result = { data: res.data, error: null };
                                return onfulfilled ? onfulfilled(result) : result;
                            }
                            // Batch delete by arbitrary filters
                            const filterKeys = Object.keys(filters);
                            if (filterKeys.length === 0) {
                                const result = { data: null, error: 'Delete requires at least one filter via .eq()' };
                                return onfulfilled ? onfulfilled(result) : result;
                            }
                            const res = await self.withAutoRefresh(() =>
                                axios.delete(`${API_URL}/data/${table}`, {
                                    params: filters,
                                    headers: self.getHeaders(),
                                })
                            );
                            const result = { data: res.data, error: null };
                            return onfulfilled ? onfulfilled(result) : result;
                        } catch (error: any) {
                            const result = { data: null, error: extractErrorMessage(error) };
                            return onfulfilled ? onfulfilled(result) : result;
                        }
                    }
                };
                return builder;
            }
        };
    }

    async rpc(name: string, params?: any) {
        try {
            const res = await this.withAutoRefresh(() =>
                axios.post(`${API_URL}/rpc/${name}`, params, {
                    headers: this.getHeaders(),
                })
            );
            return { data: res.data, error: null };
        } catch (error: any) {
            return { data: null, error: extractErrorMessage(error) };
        }
    }

    async post(path: string, body?: any) {
        try {
            const res = await this.withAutoRefresh(() =>
                axios.post(`${API_URL}${path}`, body, {
                    headers: this.getHeaders(),
                })
            );
            return { data: res.data, error: null };
        } catch (error: any) {
            return { data: null, error: extractErrorMessage(error) };
        }
    }

    async get(path: string, params?: any) {
        try {
            const res = await this.withAutoRefresh(() =>
                axios.get(`${API_URL}${path}`, {
                    params,
                    headers: this.getHeaders(),
                })
            );
            return { data: res.data, error: null };
        } catch (error: any) {
            return { data: null, error: extractErrorMessage(error) };
        }
    }

    get services() {
        const self = this;
        return {
            async invoke(name: string, options: { body: any }) {
                try {
                    const res = await self.withAutoRefresh(() =>
                        axios.post(`${API_URL}/services/${name}`, options.body, {
                            headers: self.getHeaders(),
                        })
                    );
                    return { data: res.data, error: null };
                } catch (error: any) {
                    return { data: null, error: extractErrorMessage(error) };
                }
            }
        };
    }

    auth = {
        signUp: async ({ email, password, options }: any) => {
            try {
                const res = await this.withAutoRefresh(() =>
                    axios.post(`${API_URL}/auth/signup`, { email, password, data: options?.data }, {
                        headers: this.getHeaders(),
                    })
                );
                const { accessToken, refreshToken, user } = res.data;
                // DO NOT set localStorage here, as this is used to create staff from an existing session
                return { data: { user, session: { access_token: accessToken } }, error: null };
            } catch (error: any) {
                return { data: { user: null, session: null }, error: extractErrorMessage(error) };
            }
        },
        signInWithPassword: async ({ email, password }: any) => {
            try {
                const res = await axios.post(`${API_URL}/auth/login`, { email, password });
                const { accessToken, refreshToken, user } = res.data;
                localStorage.setItem(this.tokenKey, accessToken);
                localStorage.setItem(this.refreshKey, refreshToken);
                return { data: { user, session: { access_token: accessToken } }, error: null };
            } catch (error: any) {
                return { data: null, error: extractErrorMessage(error) };
            }
        },
        signOut: async () => {
            try {
                const refreshToken = localStorage.getItem(this.refreshKey);
                await axios.post(`${API_URL}/auth/logout`, { refreshToken }, {
                    headers: this.getHeaders(),
                });
            } finally {
                localStorage.removeItem(this.tokenKey);
                localStorage.removeItem(this.refreshKey);
            }
        },
        getSession: async () => {
            const token = localStorage.getItem(this.tokenKey);
            if (!token) return { data: { session: null }, error: null };
            return { data: { session: { access_token: token } }, error: null };
        },
        getUser: async () => {
            try {
                const token = localStorage.getItem(this.tokenKey);
                if (!token) return { data: null, error: 'No token' };
                const payloadB64 = token.split('.')[1];
                if (!payloadB64) return { data: null, error: 'Invalid token format' };
                const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
                const userId = payload.id;
                if (!userId) return { data: null, error: 'No user ID in token' };

                const res = await this.withAutoRefresh(() =>
                    axios.get(`${API_URL}/data/admin_users`, {
                        params: { id: userId, limit: 1 },
                        headers: this.getHeaders(),
                    })
                );
                return { data: { user: res.data[0] || null }, error: null };
            } catch (error: any) {
                return { data: null, error: extractErrorMessage(error) };
            }
        },
        updateUser: async ({ password }: any) => {
            try {
                const res = await this.withAutoRefresh(() =>
                    axios.patch(`${API_URL}/auth/update-user`, { password }, {
                        headers: this.getHeaders(),
                    })
                );
                return { data: res.data, error: null };
            } catch (error: any) {
                return { data: null, error: extractErrorMessage(error) };
            }
        },
        resetPassword: async ({ userId, password }: any) => {
            try {
                const res = await this.withAutoRefresh(() =>
                    axios.patch(`${API_URL}/auth/reset-password`, { userId, password }, {
                        headers: this.getHeaders(),
                    })
                );
                return { data: res.data, error: null };
            } catch (error: any) {
                return { data: null, error: extractErrorMessage(error) };
            }
        }
    };
}

export const api = new ApiClient();
export const customerApi = new ApiClient('es_cust_access_token', 'es_cust_refresh_token');
// The self-hosted backend is always available
export const isApiConnected = true;


